import React from 'react';
import { ArrowClockwise, Info, WarningCircle } from '@phosphor-icons/react';

export const statusClass = (status = '') => {
  const value = String(status).toLowerCase();
  if (['active', 'available', 'boardroom_ready', 'completed', 'configured', 'healthy', 'pass', 'ready'].includes(value)) return 'sev-LOW';
  if (['demo_ready', 'degraded', 'manual_command_available', 'needs_review', 'not_configured', 'optional', 'planned', 'ready_with_conditions', 'submitted'].includes(value)) return 'sev-MODERATE';
  return 'sev-CRITICAL';
};

export const StatusBadge = ({ value, children }) => (
  <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border rounded-sm ${statusClass(value)}`}>
    {children || String(value || 'unknown').replaceAll('_', ' ')}
  </span>
);

export const HealthBadge = ({ status }) => <StatusBadge value={status}>{String(status || 'unknown').replaceAll('_', ' ')}</StatusBadge>;

export const RetryButton = ({ onClick, label = 'Retry', disabled = false }) => (
  <button type="button" onClick={onClick} disabled={disabled} className="tkp-btn-primary inline-flex items-center justify-center gap-2">
    <ArrowClockwise size={14} weight="bold" />{label}
  </button>
);

export const SkeletonCard = ({ lines = 3 }) => (
  <div className="border border-slate-200 rounded-sm bg-white p-4 animate-pulse">
    {Array.from({ length: lines }).map((_, index) => (
      <div key={index} className={`h-3 bg-slate-100 rounded-sm ${index ? 'mt-3' : ''} ${index === lines - 1 ? 'w-2/3' : 'w-full'}`} />
    ))}
  </div>
);

export const LoadingState = ({ label = 'Loading...' }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="status" aria-live="polite">
    <SkeletonCard />
    <SkeletonCard />
    <div className="border border-slate-200 rounded-sm bg-white p-4 text-sm text-slate-500 flex items-center gap-2">
      <Info size={16} weight="duotone" />{label}
    </div>
  </div>
);

export const EmptyState = ({ label, action }) => (
  <div className="border border-dashed border-slate-300 rounded-sm p-6 text-sm text-slate-500 bg-white">
    <div>{label}</div>
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorState = ({ title = 'Could not load this view', message = 'The backend did not return demo data in time.', onRetry }) => (
  <div className="border border-rose-200 bg-rose-50 rounded-sm p-5 text-sm text-rose-900">
    <div className="flex items-start gap-3">
      <WarningCircle size={20} weight="duotone" className="mt-0.5 shrink-0" />
      <div>
        <div className="font-bold">{title}</div>
        <div className="mt-1 text-rose-800">{message}</div>
        {onRetry && <div className="mt-4"><RetryButton onClick={onRetry} label="Try again" /></div>}
      </div>
    </div>
  </div>
);

export const DemoNotice = ({ children }) => (
  <div className="border border-sky-200 bg-sky-50 rounded-sm px-4 py-3 text-sm text-sky-900">
    {children || 'Partner demo uses synthetic sample data only. Demo access is disabled or protected by default.'}
  </div>
);

export const PrivacyBoundaryNotice = () => (
  <div className="border border-slate-200 rounded-sm bg-white px-4 py-3 text-sm text-slate-600">
    TALKTO+ analyzes workflow execution conditions, not individuals.
  </div>
);
